import { AddOutlined, Close, Done, InfoOutlined, UploadFile } from "@mui/icons-material";
import {
    Box,
    Checkbox,
    Chip,
    Dialog,
    DialogContent,
    Divider,
    IconButton,
    TableCell,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { ChangeEvent, useContext, useEffect, useMemo, useRef, useState } from "react";
import { currencyFormatter } from "../../Bill/Cells/Last12Summary";
import { CreateReadingData, ReadingCtx } from "../AddReadings";

type Props = {
    target: any;
    updateMode?: boolean;
    count: number;
    columnVisibility: { [key: string]: boolean };
    index: number;
    overallLineRent: number;
};

type AdditionalCharge = {
    head: number;
    amount: number;
    editable: boolean;
};

// --- Utility Functions ---
const getUnitPriceAdaptive = (components: any[], units: number) => {
    let unitsAmount = 0;
    let unitsRemaining = units;
    let unitPrice = [];

    const componentsByConsumption: { [consumption: string]: any[] } = {};
    for (const comp of components) {
        const key = comp.consumption.toString();
        if (!componentsByConsumption[key]) componentsByConsumption[key] = [];
        componentsByConsumption[key].push(comp);
    }

    const consumptionKeys = Object.keys(componentsByConsumption).map((c) =>
        parseInt(c)
    );
    const maximumRange =
        consumptionKeys.length > 0 ? Math.max(...consumptionKeys) : 0;
    const targetSubCategory =
        units > maximumRange ? "-1" : maximumRange.toString();

    const targetComps = (componentsByConsumption[targetSubCategory] || []).sort(
        (a, b) => a.start - b.start
    );

    for (const comp of targetComps) {
        if (unitsRemaining <= 0) break;
        const targetCost = comp.cost;
        let unitsInRange = 0;

        if (comp.end === -1) {
            unitsInRange = unitsRemaining;
            unitsRemaining = 0;
        } else {
            const compRange = comp.end - Math.max(0, comp.start - 1);
            if (unitsRemaining - compRange < 0) {
                unitsInRange = unitsRemaining;
                unitsRemaining = 0;
            } else {
                unitsInRange = compRange;
                unitsRemaining -= compRange;
            }
        }
        unitsAmount += targetCost * unitsInRange;
        unitPrice.push([unitsInRange, targetCost]);
    }
    return [unitsAmount, unitPrice];
};

const getUnitPrice = (meter: any, units: number) => {
    const components = meter.priceType.unitChargeRule.components;
    const lastComponent = components.find((c: any) => c.end === -1);
    if (units > (lastComponent?.start ?? 0)) {
        return lastComponent?.cost ?? 0;
    } else {
        const targetComp = components
            .filter((c: any) => c?.end !== -1)
            .find((c: any) => units >= c?.start && units <= c?.end);
        return targetComp?.cost ?? 0;
    }
};

// --- Component ---
export const ReadingRow = ({
    target,
    updateMode = false,
    count,
    columnVisibility,
    index,
    overallLineRent,
}: Props) => {
    const { readingsDataRef, setReadingsData } = useContext(ReadingCtx);

    // local state to trigger re-renders for this row
    const [rowData, setRowData] = useState<CreateReadingData>({
        select: false,
        units: 0,
        lineRent: 0,
        isLineRentEditable: false,
        additionalCharges: [],
        arrears: 0,
        feedback: { message: "", status: null },
    });

    // Sync local state with the Global Ref (Crucial for Bulk Updates)
    useEffect(() => {
        const globalData = readingsDataRef.current[target.id];
        if (globalData) {
            setRowData(globalData);
        }
    }, [readingsDataRef.current[target.id], target.id]);

    const handleSelect = (ev: any, checked: boolean) => {
        const updated = { ...rowData, select: checked };
        readingsDataRef.current[target.id] = updated;
        setRowData(updated);
        // Sync parent state if needed for calculations
        setReadingsData((prev) => ({ ...prev, [target.id]: updated }));
    };

    useEffect(() => {
        if (!readingsDataRef.current[target.id]) {
            const initial = {
                ...target,
                lineRent: target.priceType?.lineRent?.amount ?? 0,
                select: false,
                imageFile: null,
            };

            readingsDataRef.current[target.id] = initial;
            setReadingsData((prev) => ({ ...prev, [target.id]: initial }));
        }
    }, [target.id]);

    return (
        <TableRow>
            <TableCell>
                <Checkbox
                    size="small"
                    checked={rowData.select}
                    onChange={handleSelect}
                />
            </TableCell>

            {columnVisibility["Status"] && (
                <StatusCell feedback={rowData.feedback} />
            )}

            {columnVisibility["Serial no."] && <TableCell>{count}</TableCell>}

            {columnVisibility["Meter no."] && (
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {target.meterNo}
                </TableCell>
            )}

            {columnVisibility["Customer name"] && (
                <TableCell>{target.property?.customer?.name}</TableCell>
            )}

            {columnVisibility["Previous reading"] && (
                <TableCell>
                    {updateMode
                        ? target.lastReadingPrevUnits
                        : (target.lastReading ?? 0)}
                </TableCell>
            )}

            {columnVisibility["Curr. reading"] && (
                <CurrentReadingCell
                    target={target}
                    rowIndex={index}
                    rowData={rowData}
                />
            )}

            {columnVisibility["Consumed Units"] && (
                <TableCell sx={{ minWidth: "12ch" }}>
                    {Math.max(0, rowData.units - target.lastReading)}
                </TableCell>
            )}

            {columnVisibility["Line Rent"] && (
                <LRCell target={target} rowIndex={index} rowData={rowData} />
            )}

            {columnVisibility["Additional Charges"] && (
                <ACSCell
                    target={target}
                    additionalCharges={rowData.additionalCharges}
                    onChange={(acs) =>
                        setRowData(readingsDataRef.current[target.id])
                    }
                />
            )}

            {columnVisibility["Calculations"] && (
                <UnitAmountCalcCell target={target} units={rowData.units} />
            )}

            {columnVisibility["Surcharge"] && (
                <SurchargeCell
                    target={target}
                    units={rowData.units}
                    lineRent={rowData.lineRent}
                    additionalCharges={rowData.additionalCharges}
                />
            )}

            {columnVisibility["Connection charges"] && (
                <TableCell>
                    {currencyFormatter.format(
                        target.connectionChargesApplied
                            ? 0
                            : target.connectionCharges
                    )}
                </TableCell>
            )}

            {columnVisibility["Upload Image"] && (
                <UploadImageCell target={target} rowData={rowData} />
            )}
        </TableRow>
    );
};

// --- Sub-components ---

const CurrentReadingCell = ({ target, rowIndex, rowData }: any) => {
    const { readingsDataRef, setReadingsData, currReadingInputsRef } =
        useContext(ReadingCtx);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        const numVal = raw === "" ? 0 : parseInt(raw, 10);

        const updated = {
            ...readingsDataRef.current[target.id],
            units: numVal,
            select: raw !== "", // Row stays selected even if value is 0
        };

        readingsDataRef.current[target.id] = updated;
        setReadingsData((prev) => ({ ...prev, [target.id]: updated }));
    };

    const is_invalid = rowData.units < parseInt(target.lastReading || "0");

    return (
        <TableCell>
            <TextField
                type="number"
                size="small"
                value={rowData.units}
                onChange={handleChange}
                inputRef={(el) => {
                    if (el) currReadingInputsRef.current[rowIndex] = el;
                }}
                error={rowData.select && is_invalid}
                // helperText={
                //     rowData.select && is_invalid
                //         ? `Reading must be >=: ${target.lastReading}`
                //         : ""
                // }
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        currReadingInputsRef.current[rowIndex + 1]?.focus();
                    }
                }}
                sx={{ width: "15ch" }}
            />
        </TableCell>
    );
};

const LRCell = ({ target, rowIndex, rowData }: any) => {
    const { readingsDataRef, setReadingsData, currLineRentInputsRef } =
        useContext(ReadingCtx);

    const defaultLineRent = target.priceType?.lineRent?.amount ?? 0;

    const displayLineRent =
        readingsDataRef.current[target.id]?.lineRent ?? defaultLineRent;
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (!target.lineRentEditable) return;

        const raw = e.target.value;
        // Accept 0 correctly
        const numVal = raw === "" ? 0 : parseFloat(raw);

        const updated = {
            ...readingsDataRef.current[target.id],
            lineRent: numVal,
            select: true,
        };

        readingsDataRef.current[target.id] = updated;
        setReadingsData((prev) => ({ ...prev, [target.id]: updated }));
    };

    return (
        <TableCell>
            <TextField
                type="number"
                size="small"
                disabled={!target.lineRentEditable}
                value={displayLineRent}
                onChange={handleChange}
                inputRef={(el) => {
                    if (el) currLineRentInputsRef.current[rowIndex] = el;
                }}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        currLineRentInputsRef.current[rowIndex + 1]?.focus();
                    }
                }}
                sx={{ width: "12ch" }}
            />
        </TableCell>
    );
};

const ACSCell = ({
    target,
    additionalCharges,
    onChange,
}: {
    target: any;
    additionalCharges: AdditionalCharge[];
    onChange: (acs: AdditionalCharge[]) => void;
}) => {
    const { readingsDataRef, setActiveMeter } = useContext(ReadingCtx);

    const handleDeleteACS = (idx: number) => {
        const newACS = additionalCharges.filter((x, id) => id != idx);

        readingsDataRef.current = {
            ...readingsDataRef.current,
            [target.id]: {
                ...readingsDataRef.current[target.id],
                additionalCharges: newACS,
            },
        };

        onChange(newACS);

        // setReadingsData({
        //     ...readingsData,
        //     [target.id]: {
        //         ...readingsData[target.id],
        //         additionalCharges: newACS,
        //     },
        // });
    };

    console.log(additionalCharges);
    return target ? (
        <TableCell>
            <Box display="flex" alignItems="center">
                <Box display="flex" gap="2px">
                    {additionalCharges
                        ?.filter((c: any) => !c.completed)
                        .map((c, idx) => (
                            <Chip
                                key={idx}
                                variant="filled"
                                size="small"
                                label={c.amount}
                                onDelete={() => handleDeleteACS(idx)}
                            />
                        ))}
                </Box>

                <IconButton
                    size="small"
                    onClick={() => setActiveMeter(target.id)}
                >
                    <AddOutlined />
                </IconButton>
            </Box>
        </TableCell>
    ) : null;
};

const StatusCell = ({ feedback }: any) => {
    const [open, setOpen] = useState(false);
    if (!feedback || feedback.status === null)
        return (
            <TableCell>
                <InfoOutlined color="disabled" />
            </TableCell>
        );

    return (
        <TableCell>
            <IconButton size="small" onClick={() => setOpen(true)}>
                {feedback.status === "success" ? (
                    <Done color="success" />
                ) : (
                    <Close color="error" />
                )}
            </IconButton>
            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogContent>{feedback.message}</DialogContent>
            </Dialog>
        </TableCell>
    );
};

const UnitAmountCalcCell = ({ target, units }: any) => {
    const consumed = Math.max(0, units - target.lastReading);
    const isAdaptive = target.priceType.unitChargeRule.isAdaptive;

    const calcData = useMemo(() => {
        if (isAdaptive)
            return getUnitPriceAdaptive(
                target.priceType.unitChargeRule.components,
                consumed
            );
        return getUnitPrice(target, consumed);
    }, [target, units]);

    return (
        <TableCell sx={{ minWidth: "18ch" }}>
            {isAdaptive ? (
                <Box textAlign="right">
                    {(calcData[1] as any[]).map(([u, cost], idx) => (
                        <div key={idx}>{`${u} x ${cost} = ${u * cost}`}</div>
                    ))}
                    <Divider />
                    <Box fontWeight="bold">{calcData[0]}</Box>
                </Box>
            ) : (
                <>
                    {consumed} x {calcData as number} ={" "}
                    {consumed * (calcData as number)}
                </>
            )}
        </TableCell>
    );
};

const SurchargeCell = ({ target, units, lineRent, additionalCharges }: any) => {
    const surcharge = useMemo(() => {
        if (!target.priceType.surcharge) return 0;
        const pct = target.priceType.surcharge.value / 100;

        if (target.priceType.surcharge.isSurchargePct) {
            let total = 0;
            const strategies = JSON.parse(
                target.priceType.surcharge.calculationStrategy ?? "[]"
            );
            const consumed = Math.max(0, units - target.lastReading);

            for (const strat of strategies) {
                if (strat === "units")
                    total += consumed * getUnitPrice(target, consumed) * pct;
                if (strat === "lr") total += lineRent * pct;
                if (strat === "arrears") total += (target.arrears || 0) * pct;
                if (strat === "acs")
                    total +=
                        additionalCharges.reduce(
                            (sum: number, c: any) => sum + c.amount,
                            0
                        ) * pct;
            }
            return total;
        }
        return target.priceType.surcharge.value;
    }, [units, lineRent, additionalCharges]);

    return <TableCell>{currencyFormatter.format(surcharge)}</TableCell>;
};

const UploadImageCell = ({ target, rowData }: any) => {
    const { readingsDataRef, setReadingsData } = useContext(ReadingCtx);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const imageFile: File | null = rowData.imageFile ?? null;

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] ?? null;
        const updated = {
            ...readingsDataRef.current[target.id],
            imageFile: file,
        };
        readingsDataRef.current[target.id] = updated;
        setReadingsData((prev) => ({ ...prev, [target.id]: updated }));
    };

    const handleClear = () => {
        const updated = {
            ...readingsDataRef.current[target.id],
            imageFile: null,
        };
        readingsDataRef.current[target.id] = updated;
        setReadingsData((prev) => ({ ...prev, [target.id]: updated }));
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    return (
        <TableCell sx={{ whiteSpace: "nowrap" }}>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleFileChange}
                id={`img-upload-${target.id}`}
            />
            {imageFile ? (
                <Box display="flex" alignItems="center" gap={0.5}>
                    <Tooltip title={imageFile.name}>
                        <Typography
                            variant="caption"
                            sx={{
                                maxWidth: "10ch",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                display: "inline-block",
                                verticalAlign: "middle",
                            }}
                        >
                            {imageFile.name}
                        </Typography>
                    </Tooltip>
                    <IconButton size="small" onClick={handleClear}>
                        <Close fontSize="small" />
                    </IconButton>
                </Box>
            ) : (
                <Tooltip title="Upload image">
                    <IconButton
                        size="small"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <UploadFile fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
        </TableCell>
    );
};
